import { useCallback } from 'react';

import { useAtom, useAtomValue } from 'jotai';

import { userDataAtom } from 'src/stores/auth/atoms';
import { getAtomWithStorage } from 'src/utils/localStorageUtils';

interface ClientControlPopupState {
  /**
   * 팝업을 마지막으로 표시한 시간
   */
  lastDisplayTime: number;

  /**
   * isResolved가 true인 경우 팝업을 더 이상 표시하지 않음
   */
  isResolved: boolean;
}

interface UserPopupMap {
  [userId: string]: {
    [popupId: string]: ClientControlPopupState;
  };
}

const clientControlPopupStateAtom = getAtomWithStorage<UserPopupMap>('clientControlPopupState', {});

/**
 * Server Control Popup 중, Client에서 처리해야하는 동작을 구현하는 훅
 * 1. displayIntervalInMinutes를 사용한 팝업 표시 throttle 처리
 * 2. NoticePopup 팝업 표시 여부 처리
 */
export const useClientControlPopup = () => {
  const userData = useAtomValue(userDataAtom);
  const userId = userData?.userId;

  const [clientControlPopupState, setClientControlPopupState] = useAtom(
    clientControlPopupStateAtom
  );

  const setDisplayTime = useCallback(
    (popupId: string) => {
      if (!userId) return;

      setClientControlPopupState((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          [popupId]: {
            lastDisplayTime: Date.now(),
            isResolved: prev[userId]?.[popupId]?.isResolved ?? false,
          },
        },
      }));
    },
    [setClientControlPopupState, userId]
  );

  const setIsResolved = useCallback(
    (popupId: string) => {
      if (!userId) return;

      setClientControlPopupState((prev) => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          [popupId]: {
            lastDisplayTime: prev[userId]?.[popupId]?.lastDisplayTime ?? Date.now(),
            isResolved: true,
          },
        },
      }));
    },
    [setClientControlPopupState, userId]
  );

  const shouldDisplayPopup = useCallback(
    (popupId: string, displayIntervalInMinutes: number | null) => {
      if (!userId) return false;

      if (displayIntervalInMinutes === null) return true;

      const popupState = clientControlPopupState[userId]?.[popupId];
      if (!popupState) return true;
      if (popupState.isResolved) return false;
      return Date.now() - popupState.lastDisplayTime >= displayIntervalInMinutes * 60 * 1000;
    },
    [clientControlPopupState, userId]
  );

  return { setDisplayTime, setIsResolved, shouldDisplayPopup };
};
